<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Badge from 'primevue/badge'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import type { Tender, WorkflowStatus } from '@/types/tender'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'

interface TenderResponse extends Omit<Tender, 'workflowStatus'> {
  assignedAgentName?: string
  workflowStatus?: WorkflowStatus
  assignedToAgent?: string
  assignedAgentEmail?: string
  assignedAgentPhone?: string
}

// Define the enhanced tender type for this component
interface AgentTender extends Omit<Tender, 'assignedAgent'> {
  assignedAgent?: string
  assignedAgentName?: string
  agentWorkflowStatus?: WorkflowStatus
  agentNotes?: string | null
  agentAssignedAt?: string | Date | null  // Changed to match Tender type
  assignedAgentId?: string
  assignedAgentEmail?: string
  assignedAgentPhone?: string
}

import {
  EyeIcon,
  ExternalIcon,
  GlobeIcon,
  RefreshIcon,
  // TimeLineIcon,
  // FileSearchIcon,
  // IndianRupeeIcon,
  // SigmaIcon,
} from '@/icons'

// ✅ Import everything from your enhanced badgeUtils
import {
  getBadgeStatus,
  getWorkflowLabel,
} from '@/utils/badgeUtils'

// import SelectOptionPrime from '@/components/forms/SelectOption/SelectOptionPrime.vue'
import api from '@/services/api'
import { getAxiosErrorMessage } from '@/utils/errorHandler'
import type { AxiosError } from 'axios'
// import StatsCards from '@/components/cards/StatsCards.vue'
import EnvelopeIcon from '@/icons/EnvelopeIcon.vue'
import MobileIcon from '@/icons/MobileIcon.vue'
import router from '@/router'
import EmptyState from '@/components/common/EmptyState.vue'
import LoaderDefault from '@/components/common/LoaderDefault.vue'
import ErrorState from '@/components/common/ErrorState.vue'
import TooltipPortal from '@/components/common/TooltipPortal.vue'

// ============ COMPONENT STATE ============
const searchFilter = ref('')
const statusFilter = ref('')

// Define the allowed workflow statuses for this page (only price-review and criteria-review)
const allowedStatuses: WorkflowStatus[] = [
  // 'price-review',
  'submitted',
]

// Store all agents for lookup
const allAgents = ref<Agent[]>([])
const loadingAgents = ref(false)

const tenders = ref<AgentTender[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Agent interface
interface Agent {
  id: string
  name: string
  email: string
  phone?: string
  first_name?: string
  last_name?: string
}

interface AgentResponse {
  id: number | string
  first_name?: string
  last_name?: string
  username?: string
  email: string
  phone?: string
}

// ✅ Status filter dropdown options - only show allowed statuses
// const statusOptions = computed(() => {
//   return [
//     { label: 'All Status', value: '' },
//     ...allowedStatuses.map(status => ({
//       label: getWorkflowLabel(status),
//       value: status
//     }))
//   ]
// })

// ============ COMPUTED PROPERTIES ============
const filteredTenders = computed(() => {
  // Start with all tenders (which are already filtered to allowed statuses from API)
  let filtered = tenders.value

  // Apply status filter if selected
  if (statusFilter.value) {
    filtered = filtered.filter((tender) => tender.agentWorkflowStatus === statusFilter.value)
  }

  // Apply search filter
  if (searchFilter.value) {
    const searchTerm = searchFilter.value.toLowerCase()
    filtered = filtered.filter(
      (tender) =>
        tender.t247Id?.toLowerCase().includes(searchTerm) ||
        tender.referenceNo?.toLowerCase().includes(searchTerm) ||
        tender.tenderBrief?.toLowerCase().includes(searchTerm) ||
        tender.organization?.toLowerCase().includes(searchTerm) ||
        tender.location?.toLowerCase().includes(searchTerm) ||
        tender.assignedAgent?.toLowerCase().includes(searchTerm) ||
        tender.assignedAgentName?.toLowerCase().includes(searchTerm) ||
        tender.assignedAgentEmail?.toLowerCase().includes(searchTerm) ||
        tender.assignedAgentPhone?.toLowerCase().includes(searchTerm),
    )
  }

  return filtered
})

// Statistics - only counting allowed statuses
// const assignedCount = computed(() => tenders.value.length)
// const priceReviewCount = computed(
//   () => tenders.value.filter((t) => t.agentWorkflowStatus === 'price-review').length,
// )
// const criteriaReviewCount = computed(
//   () => tenders.value.filter((t) => t.agentWorkflowStatus === 'criteria-review').length,
// )

// Calculate upcoming and expired deadlines
// const upcomingDeadlinesCount = computed(() => {
//   const now = new Date()
//   const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
//   return tenders.value.filter((tender) => {
//     if (!tender.deadline) return false
//     const deadline = new Date(tender.deadline)
//     return deadline > now && deadline <= sevenDaysFromNow
//   }).length
// })

// ============ API FUNCTIONS ============
const fetchAllAgents = async () => {
  try {
    loadingAgents.value = true
    const response = await api.get('/api/users/agents')

    if (response.data.success && response.data.agents) {
      allAgents.value = response.data.agents.map((agent: AgentResponse) => ({
        id: agent.id.toString(),
        name:
          `${agent.first_name || ''} ${agent.last_name || ''}`.trim() ||
          agent.username ||
          agent.email,
        email: agent.email,
        phone: agent.phone,
        first_name: agent.first_name,
        last_name: agent.last_name,
      }))
    }
  } catch (err: unknown) {
    console.error('Error fetching agents:', err)
  } finally {
    loadingAgents.value = false
  }
}

const getAgentDetails = (agentId: string): Agent | undefined => {
  if (!agentId) return undefined
  return allAgents.value.find((agent) => agent.id === agentId.toString())
}

const fetchTenders = async () => {
  try {
    loading.value = true
    error.value = null
    const response = await api.get('/api/tenders/my-agent-tenders')

    if (response.data.success) {
      const mappedTenders: AgentTender[] = response.data.tenders
        .map((tender: TenderResponse) => {
          const agentId = tender.assignedToAgent
          let agentEmail = tender.assignedAgentEmail
          let agentPhone = tender.assignedAgentPhone

          if (agentId && (!agentEmail || !agentPhone)) {
            const agentDetails = getAgentDetails(agentId)
            if (agentDetails) {
              agentEmail = agentEmail || agentDetails.email
              agentPhone = agentPhone || agentDetails.phone
            }
          }

          // Create a new object with the correct structure
          const enhancedTender: AgentTender = {
            ...tender,
            assignedAgent: tender.assignedAgentName || 'Unknown Agent',
            assignedAgentName: tender.assignedAgentName || 'Unknown Agent',
            assignedAgentEmail: agentEmail,
            assignedAgentPhone: agentPhone,
            assignedAgentId: agentId,
            agentWorkflowStatus: (tender.workflowStatus || tender.status) as WorkflowStatus,
          }

          return enhancedTender
        })
        // Filter to only include tenders with allowed workflow statuses (price-review and criteria-review)
        .filter((tender: AgentTender) =>
          tender.agentWorkflowStatus && allowedStatuses.includes(tender.agentWorkflowStatus)
        )

      tenders.value = mappedTenders
    }
  } catch (err: unknown) {
    console.error('Error:', err)
    error.value = getAxiosErrorMessage(err as AxiosError)
  } finally {
    loading.value = false
  }
}

const formatNumber = (num: number): string => {
  if (num >= 10000000) return (num / 10000000).toFixed(2) + ' Cr'
  if (num >= 100000) return (num / 100000).toFixed(2) + ' L'
  if (num >= 1000) return (num / 1000).toFixed(2) + ' K'
  return num.toLocaleString('en-IN')
}

const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateString
  }
}

const getDaysRemaining = (deadline: string): string => {
  if (!deadline) return 'No deadline'
  try {
    const now = new Date()
    const deadlineDate = new Date(deadline)
    if (isNaN(deadlineDate.getTime())) return 'Invalid date'
    const diffTime = deadlineDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    if (diffDays < 0) return 'Expired'
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    return `${diffDays} days left`
  } catch {
    return 'Date error'
  }
}

const getDeadlineClass = (deadline: string): string => {
  if (!deadline) return 'text-gray-600'
  try {
    const now = new Date()
    const deadlineDate = new Date(deadline)
    if (isNaN(deadlineDate.getTime())) return 'text-gray-600'
    const diffTime = deadlineDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    if (diffDays < 0) return 'text-red-600 font-medium'
    if (diffDays <= 7) return 'text-orange-600 font-medium'
    return 'text-green-600 font-medium'
  } catch {
    return 'text-gray-600'
  }
}

const viewTenderDetails = (tender: Tender) => {
  router.push({
    path: `/tenders/${tender.t247Id}`,
    query: { referrer: 'submitted-tender-agent' },
  })
}

const clearFilters = () => {
  statusFilter.value = ''
  searchFilter.value = ''
}

const refreshData = async () => {
  await Promise.all([fetchAllAgents(), fetchTenders()])
}

// Tooltip management for description
const tooltipVisible = ref(false);
const tooltipText = ref('');
const tooltipTarget = ref<HTMLElement>(); // Changed from null to undefined
const tooltipPosition = ref<'top' | 'bottom' | 'left' | 'right'>('top');

// Tooltip handlers
const showTooltip = (event: MouseEvent, text: string) => {
  const target = event.currentTarget as HTMLElement;
  tooltipTarget.value = target; // This will be HTMLElement, not null
  tooltipText.value = text;
  tooltipPosition.value = 'top';
  tooltipVisible.value = true;
};

const hideTooltip = () => {
  tooltipVisible.value = false;
  tooltipTarget.value = undefined; // Changed from null to undefined
  tooltipText.value = '';
};

// Lifecycle
onMounted(() => {
  const loadData = async () => {
    await fetchAllAgents()
    await fetchTenders()
  }
  loadData()
})
</script>

<template>
  <!-- Your template remains exactly the same -->
  <AdminLayout>
    <HeadingDefault heading="Submitted Tender" />

    <!-- <div class="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <StatsCards title="Total Under Review" :value="assignedCount" :icon="SigmaIcon" />
      <StatsCards title="Price Review" :value="priceReviewCount" :icon="IndianRupeeIcon" />
      <StatsCards title="Criteria Review" :value="criteriaReviewCount" :icon="FileSearchIcon" />
      <StatsCards title="Upcoming Deadlines" :value="upcomingDeadlinesCount" :icon="TimeLineIcon" />
    </div> -->

    <!-- Error Message -->
    <div v-if="error" class="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-red-800">{{ error }}</p>
          <button
            @click="refreshData"
            class="mt-1 text-sm text-red-700 hover:text-red-600 underline"
          >
            Click here to retry
          </button>
        </div>
      </div>
    </div>

    <!-- Tender List Table -->
    <CardComponent
      className="max-w-full"
      footerClass="justify-end"
      title="Tenders List"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
      bodyClass="grid grid-cols-1"
    >
      <template #header>
        <div class="flex items-center justify-between gap-2">
          <!-- Status Filter Dropdown - Only shows Price Review and Criteria Review -->
          <!-- <SelectOptionPrime
            v-model="statusFilter"
            :options="statusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Filter by Status"
            id="review-status-filter"
            size="xss"
            class="w-48"
            :filter="true"
            :editable="false"
          /> -->

          <!-- Search Input -->
          <TextInputDefault
            v-model="searchFilter"
            placeholder="Search tenders..."
            id="searchreviewtenders"
            name="searchreviewtenders"
            type="text"
            size="xss"
          />

          <!-- Action Buttons -->
          <div class="flex gap-2 ml-auto">
            <BaseButton
              v-if="statusFilter || searchFilter"
              text="Clear Filters"
              size="xss"
              variant="ghost"
              @click="clearFilters"
            />
            <BaseButton
              size="xss"
              :icon="RefreshIcon"
              @click="refreshData"
              :loading="loading || loadingAgents"
            />
          </div>
        </div>
      </template>

      <template #body>
        <!-- Loading State -->
        <LoaderDefault
          v-if="loading || loadingAgents"
          :title="loadingAgents ? 'Loading agent information...' : 'Loading tenders under review...'"
        />

        <div v-else class="flex flex-col">
          <!-- Error State -->
          <ErrorState
            v-if="error"
            :message="error"
            :show-test-login="true"
            @retry="refreshData"
          />

          <!-- No Data State -->
          <EmptyState
            v-else-if="tenders.length === 0"
            :icon="GlobeIcon"
            title="No tenders"
            description="No tenders are currently in Submitted state"
            hint="Tenders will appear here when agents move them to review stages"
          />

          <!-- No Results after Filtering -->
          <EmptyState
            v-else-if="filteredTenders.length === 0"
            :icon="GlobeIcon"
            title="No matching tenders"
            description="No tenders match your filter criteria."
            hint="Try adjusting your search filters"
            action-text="Clear Filters"
            @action="clearFilters"
          />

          <!-- Tender Table -->
          <DataTable
            v-else-if="filteredTenders.length > 0"
            :value="filteredTenders"
            class="w-full"
            sortMode="multiple"
            scrollable
            scrollHeight="450px"
            resizableColumns
            columnResizeMode="fit"
            showGridlines
            :pt="{
              table: { style: { borderCollapse: 'separate', borderSpacing: '0' } },
              thead: { style: { position: 'sticky', top: '0', zIndex: '20' } },
              tbody: { style: { position: 'relative', zIndex: '10' } },
            }"
          >
            <!-- Tender ID Column -->
            <Column
              sortable
              frozen
              field="referenceNo"
              header="Reference No"
              headerStyle="position: sticky; left: 48px; z-index: 30;"
              bodyStyle="position: sticky; left: 48px; z-index: 25;"
            >
              <template #body="{ data }">
                <div class="flex flex-col max-w-[100px] text-xs">
                  <a
                    :href="data.tenderBriefLink"
                    target="_blank"
                    class="font-medium hover:underline flex items-center gap-1 break-all whitespace-normal"
                    v-if="data.tenderBriefLink"
                  >
                    {{ data.referenceNo }}
                    <ExternalIcon class="w-3 h-3 text-green-600 flex-shrink-0" />
                  </a>
                  <span v-else class="break-all whitespace-normal">{{ data.referenceNo }}</span>
                  <span class="text-xs text-gray-500 break-all">{{ data.t247Id }}</span>
                </div>
              </template>
            </Column>

            <!-- Deadline Column -->
            <Column sortable field="deadline" header="Deadline">
              <template #body="{ data }">
                <div class="flex flex-col text-xs">
                  <span :class="getDeadlineClass(data.deadline)">
                    {{ formatDate(data.deadline) }}
                  </span>
                  <span class="text-xs text-gray-500">
                    {{ getDaysRemaining(data.deadline) }}
                  </span>
                </div>
              </template>
            </Column>

            <!-- Agent Workflow Status Column -->
            <Column sortable field="agentWorkflowStatus" header="Review Status">
              <template #body="{ data }">
                <Badge
                  :value="getWorkflowLabel(data.agentWorkflowStatus)"
                  :class="'p-badge-' + getBadgeStatus(data.agentWorkflowStatus)"
                />
              </template>
            </Column>

            <!-- Assigned Agent Column with Contact Info -->
            <Column sortable field="assignedAgent" header="Assigned Agent">
              <template #body="{ data }">
                <div class="flex flex-col space-y-1">
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-medium text-gray-800">
                      {{ data.assignedAgent || data.assignedAgentName || 'Not Assigned' }}
                    </span>
                  </div>
                  <div
                    v-if="data.assignedAgentEmail || data.assignedAgentPhone"
                    class="space-y-1.5 text-xs"
                  >
                    <div
                      v-if="data.assignedAgentEmail && data.assignedAgentEmail !== 'Not available'"
                      class="flex items-center gap-1 text-gray-600 group"
                    >
                      <EnvelopeIcon class="w-4 h-4" />
                      <a
                        :href="`mailto:${data.assignedAgentEmail}`"
                        class="truncate hover:text-blue-600 hover:underline transition-colors"
                        :title="`Email: ${data.assignedAgentEmail}`"
                      >
                        {{ data.assignedAgentEmail }}
                      </a>
                    </div>
                    <div
                      v-if="data.assignedAgentPhone && data.assignedAgentPhone !== 'Not available'"
                      class="flex items-center gap-1 text-gray-600 group"
                    >
                      <MobileIcon class="w-4 h-4" />
                      <a
                        :href="`tel:${data.assignedAgentPhone}`"
                        class="truncate hover:text-green-600 hover:underline transition-colors"
                        :title="`Phone: ${data.assignedAgentPhone}`"
                      >
                        {{ data.assignedAgentPhone }}
                      </a>
                    </div>
                  </div>
                </div>
              </template>
            </Column>

            <!-- Assigned Date Column -->
            <Column sortable field="agentAssignedAt" header="Assigned Date">
              <template #body="{ data }">
                <div class="text-xs">
                  {{ formatDate(data.agentAssignedAt ? String(data.agentAssignedAt) : '') }}
                </div>
              </template>
            </Column>

            <!-- Type Column -->
            <Column sortable field="type" header="Type">
              <template #body="{ data }">
                <Badge
                  :value="data.sheetType"
                  :class="'p-badge-' + getBadgeStatus(data.sheetType?.toLowerCase())"
                />
              </template>
            </Column>

            <!-- Tender Brief Column -->
            <Column sortable field="tenderBrief" header="Description">
              <template #body="{ data }">
                <div
                  class="line-clamp-2 text-xs cursor-help"
                  @mouseenter="(event) => showTooltip(event, data.tenderBrief)"
                  @mouseleave="hideTooltip"
                >
                  {{ data.tenderBrief }}
                </div>
              </template>
            </Column>

            <!-- Organization Column -->
            <Column sortable field="organization" header="Organization">
              <template #body="{ data }">
                <div class="text-xs">{{ data.organization }}</div>
              </template>
            </Column>

            <!-- Location Column -->
            <Column sortable field="location" header="Location">
              <template #body="{ data }">
                <span class="text-xs">{{ data.location }}</span>
              </template>
            </Column>

            <!-- Estimated Cost Column -->
            <Column sortable field="estimatedCost" header="Estimated Cost">
              <template #body="{ data }">
                <div class="text-xs font-medium">₹{{ formatNumber(data.estimatedCost) }}</div>
              </template>
            </Column>

            <!-- Action Column -->
            <Column
              frozen
              field="action"
              header="Actions"
              headerStyle="width: 100px; position: sticky; right: 0; z-index: 30;"
              bodyStyle="position: sticky; right: 0; z-index: 25;"
            >
              <template #body="{ data }">
                <div class="flex gap-2">
                  <BaseButton
                    size="xss"
                    :aria-label="`view ${data.t247Id} details`"
                    @click="viewTenderDetails(data)"
                    variant="secondary"
                    :icon="EyeIcon"
                    tooltip="View Tender Details"
                    tooltipPosition="bottom"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>

      <template #footer v-if="!loading && !error && filteredTenders.length > 0">
        <div class="flex justify-between items-center w-full">
          <div class="text-xs text-gray-500">
            Showing {{ filteredTenders.length }} of {{ tenders.length }} tenders under review
            <span v-if="statusFilter">
              (filtered by: {{ getWorkflowLabel(statusFilter as WorkflowStatus) }})
            </span>
          </div>
          <div class="flex items-center gap-2 text-xs text-gray-500">
            <span v-if="loadingAgents" class="flex items-center gap-1">
              <i
                class="animate-spin rounded-full h-3 w-3 border border-current border-t-transparent"
              ></i>
              Updating agent info...
            </span>
            <span v-else> {{ allAgents.length }} agents loaded </span>
            <span class="ml-4">Last updated: {{ new Date().toLocaleTimeString() }}</span>
          </div>
        </div>
      </template>
    </CardComponent>
  </AdminLayout>

  <TooltipPortal
    :visible="tooltipVisible"
    :text="tooltipText"
    :target="tooltipTarget"
    position="auto"
    :max-width="'400px'"
    :multiline="true"
  />
</template>

<style scoped>
.group:hover .group-hover\:opacity-100 {
  opacity: 1;
}

.transition-colors {
  transition: color 0.2s ease;
}
</style>
